import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Collapse, Space, Spin } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import min from 'lodash/min';
import { useArticles } from 'apiTyped/hooks/articles';

import Icon from 'components/Icon';
import CountryLabel from 'components/CountryLabel';
import WSEmpty from 'components/Empty';
import Loader from 'components/Loader';
import MainArticle from 'modules/Media/components/UserArticles/MainArticle';
import { UserArticlesContainer } from 'modules/Media/components/UserArticles/UserArticleContainer';
import moment from 'moment/moment';
import ShowMoreButton from 'components/ShowMoreButton';

const isMobile = window.innerWidth < 845;

// import {
//   getLanguageArticle,
//   getLanguageClient,
//   translateArticlePreview
// } from '../../../../utils/translation';
// import set from 'lodash/set';

const ExpandIcon = ({ active = true }: { active?: boolean }) => (
  <>
    {active ? (
      <Space>
        <EyeInvisibleOutlined style={{ fontSize: 16, color: '#868e96' }} />
        <p>{I18n.t('media.hideUserArticles')}</p>
      </Space>
    ) : (
      <Space>
        <EyeOutlined style={{ fontSize: 16, color: '#868e96' }} />
        <p>{I18n.t('media.showUserArticles')}</p>
      </Space>
    )}
  </>
);

const ArticleNavigationContainer = styled.div<{
  disabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0 12px;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 10px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    margin-bottom: 0;
  }

  &:hover {
    cursor: pointer;
  }
`;

const NavigationItemContainer = styled.div<{
  disabled: boolean;
}>`
  font-size: 17px;
  color: ${(props) => (props.disabled ? '#202529' : '#e25d34')};

  &:hover {
    cursor: pointer;
  }
`;

const NavigationArticles = ({
  articles,
  nbArticlesToShow,
  showAllArticles,
  currentArticleIndex,
  handlePreviousArticle,
  handleNextArticle,
  onClick
}: {
  articles: any[];
  nbArticlesToShow: number;
  currentArticleIndex: number;
  showAllArticles: boolean;
  handlePreviousArticle: () => void;
  handleNextArticle: () => void;
  onClick: () => void;
}) => {
  if (!isMobile) {
    return (
      <ArticleNavigationContainer>
        <NavigationItemContainer
          onClick={() => handlePreviousArticle()}
          disabled={currentArticleIndex === 0}
        >
          <Icon alt="left" icon="chevronLeft" />
        </NavigationItemContainer>
        <NavigationItemContainer
          onClick={handleNextArticle}
          disabled={currentArticleIndex === articles.length - 1}
        >
          <Icon alt="right" icon="chevronRight" />
        </NavigationItemContainer>
      </ArticleNavigationContainer>
    );
  }

  if (articles.length <= nbArticlesToShow) {
    return null;
  }

  if (showAllArticles) {
    // show less
    return (
      <div className="show-more-button">
        <ShowMoreButton
          label={`${I18n.t('travel.seeLess')} (${nbArticlesToShow})`}
          onClick={() => onClick()}
        />
      </div>
    );
  }

  // show more
  return (
    <div className="show-more-button">
      <ShowMoreButton
        label={`${I18n.t('travel.seeMoreTable')} (+${
          articles.length - nbArticlesToShow
        })`}
        onClick={() => onClick()}
      />
    </div>
  );
};

export const UserArticles = () => {
  const nbToShow = isMobile ? 3 : 5;
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const { isLoadingArticles, articlesResponse } = useArticles({
    isFeatured: true,
    limit: 10
  });
  const [loadingTranslate, setLoadingTranslate] = useState<boolean>(true);
  const [articles, setArticles] = useState<any[]>([]);
  const [showAllArticles, setShowAllArticles] = useState<boolean>(false);

  // @TODO use cache reactQueries for keep translate
  // const getTranslateArticles = async () => {
  //   const posts = get(articlesResponse, 'results', []);
  //   return Promise.all(
  //     posts.map(async (article) => {
  //       const translation =
  //         getLanguageClient() !== getLanguageArticle(article)
  //           ? await translateArticlePreview(
  //               article.id,
  //               article.content.title,
  //               article.content.body
  //             )
  //           : false;
  //
  //       if (translation) {
  //         set(article, 'content.title', translation.title);
  //         set(article, 'content.body', translation.body);
  //       }
  //       return article;
  //     })
  //   );
  // };

  useEffect(() => {
    const prepare = async () => {
      // const listArticles = await getTranslateArticles();
      const listArticles = get(articlesResponse, 'results', []);
      setArticles(listArticles);
    };

    setLoadingTranslate(true);
    prepare().then(() => {
      setLoadingTranslate(false);
    });
  }, [articlesResponse]);

  const nbSidesArticles = useMemo(() => {
    if (!articlesResponse) return 0;
    if (isMobile && showAllArticles) return articlesResponse.count;
    return min([articlesResponse.count, nbToShow]) || 0;
  }, [articlesResponse, showAllArticles]);

  const indexedArticles = articlesResponse?.results.map((article, index) => ({
    ...article,
    index
  }));

  const articlesToShow = useMemo(() => {
    if (!indexedArticles || !articlesResponse) return [];

    const startIndex = currentArticleIndex;

    const totalObjects = articlesResponse.results.length;
    const result = [];

    for (let i = 0; i < nbSidesArticles; i++) {
      const currentIndex = (startIndex + i + 1) % totalObjects;
      result.push(indexedArticles[currentIndex]);
    }

    return result;
  }, [articlesResponse, currentArticleIndex, nbSidesArticles]);

  const handleNextArticle = () => {
    if (!articlesResponse) return;

    const nextArticleIndex =
      currentArticleIndex < articlesResponse.results.length - 1
        ? currentArticleIndex + 1
        : currentArticleIndex;

    setCurrentArticleIndex(nextArticleIndex);
  };

  const handlePreviousArticle = () => {
    const previousArticleIndex =
      currentArticleIndex > 0 ? currentArticleIndex - 1 : currentArticleIndex;

    setCurrentArticleIndex(previousArticleIndex);
  };

  const handleShowAllArticles = () => {
    setShowAllArticles(!showAllArticles);
  };

  const currentArticle = useMemo(() => {
    if (!indexedArticles) return null;

    return indexedArticles[currentArticleIndex];
  }, [currentArticleIndex, indexedArticles]);

  const imageUrl = get(currentArticle, 'content.image_url');
  const countryOrigin = get(
    currentArticle,
    'content.source.country_origin.code'
  );

  return (
    <UserArticlesContainer>
      <Collapse
        ghost
        collapsible="icon"
        expandIconPosition="end"
        expandIcon={({ isActive }) =>
          !isMobile && <ExpandIcon active={isActive} />
        }
        defaultActiveKey={['userArticles']}
        items={[
          {
            key: 'userArticles',
            label: (
              <h1 className="userArticlesTitle">
                {I18n.t('media.userArticles')}
              </h1>
            ),
            children: (
              <Spin
                spinning={isLoadingArticles || loadingTranslate}
                style={{ maxHeight: '100%' }}
                indicator={
                  <div>
                    <Loader />
                  </div>
                }
              >
                <div style={{ minHeight: 80 }}>
                  {currentArticle && (
                    <div className="userArticlesContent">
                      <div className="articleContent">
                        <div className="mainArticleImageContainer">
                          {imageUrl && (
                            <img
                              className="mainArticleImage"
                              src={imageUrl}
                              alt="image-main-article"
                            />
                          )}
                          {countryOrigin && (
                            <div className="mainArticleCountry">
                              <CountryLabel
                                country={null}
                                city={null}
                                styleLabel={null}
                                name={countryOrigin}
                                onlyFlag
                                style={null}
                                styleFlag={null}
                                customStyles={`.country-label-flag{width:24px;font-size:24px;}`}
                              />
                            </div>
                          )}
                        </div>
                        <MainArticle article={currentArticle} showBody={true} />
                      </div>
                      {isMobile && (
                        <div className="articleSeparatorContainer">
                          <div className="articleSeparator" />
                        </div>
                      )}
                      <div>
                        <div className="articlesContainer">
                          {articlesToShow.map(
                            ({
                              id: postId,
                              content: {
                                title = '',
                                image_url = '',
                                pubdate = '',
                                source = {}
                              },
                              index
                            }) => (
                              <React.Fragment key={`article-${index}`}>
                                <div className="articleContainer">
                                  <div className="articleImageContainer">
                                    <img
                                      src={image_url || undefined}
                                      className="articleImage"
                                      alt="articleImage"
                                    />
                                  </div>
                                  <Link to={`article/${postId}/`}>
                                    <div className="articleBody">
                                      <h1 className="articleTitle">{title}</h1>
                                      <div className="mainArticleInfo">
                                        {moment(pubdate).format('DD MMMM YYYY')}{' '}
                                        {I18n.t('media.fromSource')}{' '}
                                        <span>{source.name}</span>
                                      </div>
                                    </div>
                                  </Link>
                                </div>
                              </React.Fragment>
                            )
                          )}
                        </div>
                        <NavigationArticles
                          articles={articles}
                          nbArticlesToShow={nbToShow}
                          currentArticleIndex={currentArticleIndex}
                          showAllArticles={showAllArticles}
                          handlePreviousArticle={handlePreviousArticle}
                          handleNextArticle={handleNextArticle}
                          onClick={handleShowAllArticles}
                        />
                      </div>
                    </div>
                  )}
                  {!isLoadingArticles && isEmpty(articles) && (
                    <WSEmpty
                      description={
                        "Aucun article sur vous n'a été trouvé pour le moment"
                      }
                    />
                  )}
                </div>
              </Spin>
            )
          }
        ]}
        style={{
          display: 'grid',
          gap: 16
        }}
      />
    </UserArticlesContainer>
  );
};
